import React from "react";

import { useGetApplicationQuery, useGetPlanCostsQuery, useGetPlansQuery } from "EmployeeApplication/api";
import { LongTermDisabilityContextProvider } from "EmployeeCoverage/Components/LongTermDisability/longTermDisability.context";
import { LongTermDisabilityEnrollmentModals } from "EmployeeCoverage/Components/LongTermDisability/longTermDisability.modals";
import { LongTermDisabilityMultiplePlans } from "EmployeeCoverage/Components/LongTermDisability/longTermDisability.multiplePlans";
import { LongTermDisabilityPlan, PlanCost } from "Utils/types";
import { BasicLoading } from "ZeroRuby";

import { LongTermDisabilitySinglePlan } from "./longTermDisability.singlePlan";

type Data =
  | {
      isLoading: true;
      plans: any;
      planCosts: any;
    }
  | {
      isLoading: false;
      plans: Array<LongTermDisabilityPlan>;
      planCosts: Array<PlanCost>;
    };

const useData = (): Data => {
  const { isLoading: isLoadingApplication, data: { employeePolicy: { id } = {} } = {} } = useGetApplicationQuery();
  const { isLoading: isLoadingPlans, data: { plans = [] } = {} } = useGetPlansQuery(
    {
      id,
      type: "long_term_disability",
    },
    { skip: !id },
  );
  const { isLoading: isLoadingCosts, data: { planCosts } = {} } = useGetPlanCostsQuery(
    {
      id,
      type: "long_term_disability",
    },
    { skip: plans.length === 0 },
  );

  return {
    isLoading: isLoadingApplication || isLoadingPlans || isLoadingCosts,
    plans,
    planCosts,
  } as Data;
};

/**
 * This component manages what to render as long term disability plans and costs are loaded.
 */
const StateManager = () => {
  const { isLoading, plans = [], planCosts = [] } = useData();
  if (isLoading) {
    return <BasicLoading />;
  }
  if (plans.length === 1) {
    return <LongTermDisabilitySinglePlan plan={plans[0]} planCost={planCosts.find(({ planID }) => planID === plans[0].id)} />;
  }
  if (plans.length > 1) {
    return <LongTermDisabilityMultiplePlans plans={plans} planCosts={planCosts} />;
  }
  return null;
};

export const LongTermDisabilityPage = () => (
  <LongTermDisabilityContextProvider>
    <StateManager />
    <LongTermDisabilityEnrollmentModals />
  </LongTermDisabilityContextProvider>
);
